import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PaginatorModule } from 'primeng/paginator';
import { KeycloakService } from 'keycloak-angular';
import { AclApdApiService } from '@angular-workspace/services';
import { ActionDropdownTriggerDirective } from '../../directives/action-dropdown-trigger.directive';
import { FormatService } from '../../services/format.service';
import { ToasterService } from '../../../services/toaster.service';

export interface AssetRequest {
  id: string;
  itemId?: string;
  name: string;
  type: string;
  requestType: string;
  purpose: string;
  organisation: string;
  requestAs?: 'Individual' | 'Organisation';
  requestCreatedAt: string;
  status: 'approved' | 'rejected' | 'withdrawn' | 'pending';
}

export type RequestStatus = 'approved' | 'rejected' | 'withdrawn' | 'pending';

@Component({
  selector: 'app-my-request-shared',
  standalone: true,
  imports: [CommonModule, PaginatorModule, ActionDropdownTriggerDirective],
  templateUrl: './my-request-shared.component.html',
  styleUrl: './my-request-shared.component.scss',
})
export class MyRequestSharedComponent implements OnInit {
  activeStatus: RequestStatus = 'pending';
  loading = false;
  emptyMessage: SafeHtml = '';
  expandedRowKeys: { [key: string]: boolean } = {};

  statusTabs: Array<{ key: RequestStatus; label: string; count: number; icon: string }> = [
    { key: 'pending', label: 'Pending', count: 3, icon: 'clock' },
    { key: 'withdrawn', label: 'Withdrawn', count: 1, icon: 'arrow-up' },
    { key: 'approved', label: 'Approved', count: 5, icon: 'check-circle' },
    { key: 'rejected', label: 'Rejected', count: 2, icon: 'x-circle' },
  ];

  requestsData: AssetRequest[] = [];
  openDropdownId: string | null = null;
  private dropdownOpenedAt = 0;

  pagination = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    serverSide: true,
  };

  constructor(
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    private aclApdApiService: AclApdApiService,
    private formatService: FormatService,
    private toasterService: ToasterService,
    private keycloak: KeycloakService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.waitForKeycloakReady();
    this.route.queryParams.subscribe((params) => {
      const statusParam = params['status'];
      if (statusParam && this.isValidStatus(statusParam)) {
        this.activeStatus = statusParam as RequestStatus;
      }
      this.loadRequests(this.activeStatus);
    });
  }

  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();
      if (keycloakInstance?.authenticated && keycloakInstance?.token) return;

      const maxWaitTime = 5000;
      const checkInterval = 100;
      const startTime = Date.now();
      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) return;
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
      }
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
    }
  }

  async loadRequests(status: RequestStatus, showLoading: boolean = true): Promise<void> {
    if (showLoading) this.loading = true;
    try {
      const statusMap: { [key in RequestStatus]: string } = {
        approved: 'GRANTED',
        rejected: 'REJECTED',
        pending: 'PENDING',
        withdrawn: 'WITHDRAWN',
      };

      const response = await this.aclApdApiService.getUserAccessRequests(
        this.pagination.currentPage,
        this.pagination.pageSize,
        statusMap[status]
      );

      let requests: any[] = [];
      if (response && response['result'] && Array.isArray(response['result'])) {
        requests = response['result'];
      } else if (Array.isArray(response)) {
        requests = response;
      }

      this.requestsData = requests.map((item: any) => {
        const requestAs = item.additionalInfo?.requestAs === 'Organisation' ? 'Organisation' : 'Individual';
        const organisationName =
          item.additionalInfo?.organisationName ||
          item.organisationName ||
          item.organisation_name ||
          item.orgName ||
          item.org_name ||
          item.organisation?.name ||
          item.organisation?.organisationName ||
          '';

        return {
          id: item.id || item.requestId || '',
          itemId: item.asset?.itemId || item.itemId || '',
          name: item.asset?.assetName || item.assetName || item.name || '',
          type: item.asset?.assetType || item.assetType || item.type || '',
          requestType: this.toPascalCase(item.requestType || item.accessType || ''),
          purpose: item.additionalInfo?.purposeOfRequest || item.purpose || item.reason || '',
          organisation: organisationName || 'N/A',
          requestAs,
          requestCreatedAt: item.createdAt || item.updatedAt || item.requestedAt || item.requestCreatedAt || '',
          status,
        } as AssetRequest;
      });

      const responseData = response as any;
      if (responseData && responseData['paginationInfo']) {
        this.pagination.totalItems = responseData['paginationInfo'].totalCount || this.requestsData.length;
      } else {
        this.pagination.totalItems = this.requestsData.length;
      }

      this.updateEmptyMessage(status);
    } catch (error) {
      console.error('Error loading access requests:', error);
      this.requestsData = [];
      this.updateEmptyMessage(status);
    } finally {
      if (showLoading) this.loading = false;
    }
  }

  isValidStatus(status: string): status is RequestStatus {
    return ['approved', 'rejected', 'withdrawn', 'pending'].includes(status);
  }

  filterByStatus(status: RequestStatus): void {
    this.activeStatus = status;
    this.pagination.currentPage = 1;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { status },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.loadRequests(status);
  }

  updateEmptyMessage(status: RequestStatus): void {
    const statusLabels = {
      approved: 'Approved',
      pending: 'Pending',
      rejected: 'Rejected',
      withdrawn: 'Withdrawn',
    };
    const statusLabel = statusLabels[status];
    const message = `No <span class="empty-message-status">${statusLabel}</span> requests found`;
    this.emptyMessage = this.sanitizer.bypassSecurityTrustHtml(message);
  }

  getAssetTypeIconSrc(type: string): string | null {
    const lowerType = type.toLowerCase();
    if (lowerType.includes('databank') || lowerType.includes('dataset')) return '/asset-dataBank.svg';
    return null;
  }

  getAssetTypeIcon(type: string): SafeHtml {
    let iconPath = '';
    const lowerType = type.toLowerCase();
    if (lowerType.includes('aimodel') || lowerType.includes('model') || lowerType.includes('ai')) {
      iconPath =
        '<path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
    } else if (lowerType.includes('usecase') || lowerType.includes('use case') || lowerType.includes('apps')) {
      iconPath =
        '<path d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
    }
    return this.sanitizer.bypassSecurityTrustHtml(iconPath);
  }

  formatAssetTypeLabel(type: string): string {
    return this.formatService.formatAssetType(type);
  }

  get displayedRequests(): AssetRequest[] {
    if (!this.pagination.enabled) return this.requestsData;
    if (this.pagination.serverSide) return this.requestsData;
    const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
    const end = start + this.pagination.pageSize;
    return this.requestsData.slice(start, end);
  }

  get shouldShowPagination(): boolean {
    if (!this.pagination.enabled || this.pagination.totalItems === 0) return false;
    return this.pagination.totalItems > this.pagination.pageSize;
  }

  onPageChange(event: any): void {
    const page = Math.floor(event.first / event.rows) + 1;
    this.pagination.currentPage = page;
    this.loadRequests(this.activeStatus);
  }

  toggleActionDropdown(event: Event, row: AssetRequest): void {
    event.preventDefault();
    event.stopPropagation();
    const rowId = row.id;
    const wasOpen = this.openDropdownId === rowId;
    this.openDropdownId = wasOpen ? null : rowId;
    if (!wasOpen && this.openDropdownId === rowId) {
      this.dropdownOpenedAt = Date.now();
      setTimeout(() => {
        const button = (event.target as HTMLElement).closest('.action-btn') as HTMLElement;
        const dropdown = document.querySelector(`[data-dropdown-id="${rowId}"]`) as HTMLElement;
        if (button && dropdown) {
          const rect = button.getBoundingClientRect();
          dropdown.style.position = 'fixed';
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.left = `${rect.left}px`;
          dropdown.style.width = `${rect.width}px`;
          dropdown.style.minWidth = `${rect.width}px`;
        }
      }, 0);
    }
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
  }

  isDropdownOpen(row: AssetRequest): boolean {
    return this.openDropdownId === row.id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.closest('.action-cell-wrapper') || target.closest('.action-dropdown-menu')) return;
    if (Date.now() - this.dropdownOpenedAt < 150) return;
    if (this.openDropdownId !== null) this.closeActionDropdown();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.openDropdownId !== null) this.closeActionDropdown();
  }

  onActionClick(action: string, row: AssetRequest): void {
    this.closeActionDropdown();
    switch (action) {
      case 'view':
        if (row.itemId) {
          const url = this.router.serializeUrl(this.router.createUrlTree(['/dataset', row.itemId]));
          window.open(url, '_blank');
        } else {
          console.warn('ItemId not available for request:', row);
        }
        break;
      case 'withdraw':
        this.handleWithdraw(row);
        break;
      default:
        console.log('Unknown action:', action, row);
    }
  }

  async handleWithdraw(request: AssetRequest): Promise<void> {
    if (request.status !== 'pending' || !request.id) return;

    const originalRequests = [...this.requestsData];
    const originalTotalItems = this.pagination.totalItems;
    this.requestsData = this.requestsData.filter((req) => req.id !== request.id);
    if (this.pagination.totalItems > 0) this.pagination.totalItems--;

    try {
      await this.aclApdApiService.withdrawRequest(request.id);
      this.toasterService.success("You've Successfully withdrawn your request");
      this.loadRequests(this.activeStatus, false).catch((error) => {
        console.error('Error reloading requests after withdrawal:', error);
      });
    } catch (error: any) {
      console.error('Error withdrawing request:', error);
      if (error?.error?.detail || error?.detail) {
        const errorMessage = error?.error?.detail || error?.detail;
        this.toasterService.error(errorMessage);
      }
      this.requestsData = originalRequests;
      this.pagination.totalItems = originalTotalItems;
      this.loading = false;
    }
  }

  toPascalCase(str: string): string {
    if (!str) return '';
    return str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  formatDatePart(dateString: string): string {
    return this.formatService.formatDateOnly(dateString);
  }

  formatTimePart(dateString: string): string {
    return this.formatService.formatTimeOnly(dateString);
  }

  toggleExpansion(requestId: string): void {
    if (this.expandedRowKeys[requestId]) {
      const newKeys = { ...this.expandedRowKeys };
      delete newKeys[requestId];
      this.expandedRowKeys = newKeys;
    } else {
      this.expandedRowKeys = { ...this.expandedRowKeys, [requestId]: true };
    }
  }

  isRowExpanded(requestId: string): boolean {
    return !!this.expandedRowKeys[requestId];
  }
}
